import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { api } from "../lib/api";
import { useTitle } from "../App";

export default function SolutionDetail() {
  const { slug } = useParams();
  const [solution, setSolution] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useTitle(solution?.title || "Solution");

  useEffect(() => {
    setSolution(null); setNotFound(false);
    api.get(`/content/solutions/${slug}`).then((r) => setSolution(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-midnight text-mist">
        <Navbar />
        <div className="container-ek flex min-h-[70vh] flex-col items-center justify-center text-center">
          <h1 className="font-display text-4xl font-bold">Solution not found</h1>
          <Link to="/solutions" className="btn-primary mt-8">Back to Solutions</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!solution) return <div className="min-h-screen bg-midnight" />;

  return (
    <div data-testid={`solution-page-${solution.slug}`} className="bg-midnight text-mist">
      <Navbar />
      <section className="grain pt-[76px]">
        <div className="container-ek py-20 sm:py-28">
          <Reveal>
            <Link to="/solutions" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-mist"><ArrowLeft size={14} /> All solutions</Link>
            <p className="eyebrow mb-4">Marketing & Growth</p>
            <h1 className="max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-6xl">{solution.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-400">{solution.summary}</p>
            <div className="mt-7 flex flex-wrap gap-2">
              {(solution.objectives || []).map((o) => (
                <span key={o} className="rounded-full border border-teal/30 bg-teal/[0.07] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-teal">{o}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 text-slatetext sm:py-28">
        <div className="container-ek grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">Who this is for</h2>
            <p className="mt-4 text-base leading-relaxed text-slatetext/75">{solution.audience}</p>
            <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">The problem it solves</h2>
            <p className="mt-4 text-base leading-relaxed text-slatetext/75">{solution.problem}</p>
            <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight text-midnight sm:text-3xl">How we approach it</h2>
            <ol className="mt-6 space-y-5">
              {(solution.approach || []).map((a, i) => (
                <li key={a} className="flex items-start gap-5">
                  <span className="font-display text-2xl font-bold text-violet/30">0{i + 1}</span>
                  <p className="pt-1 text-sm leading-relaxed text-slatetext/80">{a}</p>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-2xl border border-midnight/[0.08] bg-white p-8">
              <h3 className="font-display text-lg font-semibold text-midnight">Concrete deliverables</h3>
              <ul className="mt-5 space-y-3.5">
                {(solution.deliverables || []).map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-slatetext/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" /> {d}
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-midnight/[0.08] pt-6">
                <p className="text-xs leading-relaxed text-slatetext/60">
                  Scope is confirmed in conversation. We only commit to what we can actually deliver.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 text-center sm:py-24">
        <Reveal>
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">The next step is a conversation.</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
            Bring the objective; we will bring the questions. A clear brief emerges faster than you would expect.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact?category=grow-my-brand" data-testid="solution-cta" className="btn-primary">Discuss My Growth Plan <ArrowRight size={15} /></Link>
            <Link to="/partnerships" className="btn-ghost">Explore a Partnership</Link>
          </div>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
